import { useEffect, useMemo, useState } from "react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentCheckIcon,
  InboxArrowDownIcon,
} from "@heroicons/react/24/outline";

import AwaitingInventoryTable from "../../components/awaitingInventory/AwaitingInventoryTable";
import CollapsibleSection from "../../components/shared/CollapsibleSection";
import HeadlineStat from "../../components/shared/HeadlineStat";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useVehicleStats from "../../data/useVehicleStats";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import PriceFormat from "../../utils/priceFormat";

const numberOrDash = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? "—"
    : value.toLocaleString("en-GB");

const moneyOrDash = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : PriceFormat(value);

export default function AwaitingInventory() {
  const { setInfo } = usePageNameContext();
  const { stats } = useVehicleStats();
  const [statsOpen, setStatsOpen] = useState(true);

  useEffect(() => {
    setInfo({
      name: "Vehicles Awaiting Inventory",
      desc: "Decide which parts to remove from vehicles arriving on site or already in the yard.",
    });
  }, []);

  const memoStats = useMemo(() => {
    if (stats.isLoading || !stats.data) return undefined;
    return stats.data.data;
  }, [stats.isLoading, stats.data]);

  const userBreakdown = memoStats?.userInventoryToday ?? [];

  // Totals for the footer row + fallback for the "Inventoried Today" headline
  // when the backend hasn't populated the dedicated field yet.
  const totals = useMemo(() => {
    return userBreakdown.reduce(
      (acc, row) => ({
        vehicles: acc.vehicles + (row.vehiclesInventoried ?? 0),
        parts: acc.parts + (row.partsAdded ?? 0),
        value: acc.value + (row.totalValue ?? 0),
      }),
      { vehicles: 0, parts: 0, value: 0 }
    );
  }, [userBreakdown]);

  const inventoriedToday =
    memoStats?.inventoriedToday ??
    (userBreakdown.length > 0 ? totals.vehicles : null);

  return (
    <>
      {stats.isLoading ? (
        <LoadingWheel />
      ) : (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setStatsOpen((v) => !v)}
            className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50"
            aria-expanded={statsOpen}
          >
            <span>
              {statsOpen
                ? "Hide inventory insights"
                : "Show inventory insights"}
            </span>
            {statsOpen ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>

          <CollapsibleSection isOpen={statsOpen}>
            <div className="mt-2 grid grid-cols-1 gap-3 px-1 pb-2 sm:grid-cols-2">
              <HeadlineStat
                value={numberOrDash(memoStats?.awaitingInventory)}
                label="Vehicles Awaiting Inventory"
                tone="amber"
                icon={InboxArrowDownIcon}
              />
              <HeadlineStat
                value={numberOrDash(inventoriedToday)}
                label="Vehicles Inventoried Today"
                tone="emerald"
                icon={ClipboardDocumentCheckIcon}
              />
            </div>

            <div className="mb-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                  Today's Inventory by User
                </span>
                <span className="text-[11px] font-medium text-slate-500">
                  {userBreakdown.length}{" "}
                  {userBreakdown.length === 1 ? "user" : "users"}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">User</th>
                      <th className="px-3 py-2 text-right font-semibold">
                        Vehicles Inventoried
                      </th>
                      <th className="px-3 py-2 text-right font-semibold">
                        Parts Added
                      </th>
                      <th className="px-3 py-2 text-right font-semibold">
                        Total Value
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {userBreakdown.length === 0 ? (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-3 py-6 text-center text-xs text-slate-400"
                        >
                          No inventory activity yet today.
                        </td>
                      </tr>
                    ) : (
                      userBreakdown.map((row) => (
                        <tr key={row.userId ?? row.userName} className="hover:bg-slate-50">
                          <td className="px-3 py-2 text-slate-800">
                            {row.userName}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-800">
                            {numberOrDash(row.vehiclesInventoried)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-800">
                            {numberOrDash(row.partsAdded)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                            {moneyOrDash(row.totalValue)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {userBreakdown.length > 0 && (
                    <tfoot className="border-t border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wide">
                      <tr>
                        <td className="px-3 py-2 font-semibold text-slate-600">
                          Total
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-slate-800">
                          {numberOrDash(totals.vehicles)}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-slate-800">
                          {numberOrDash(totals.parts)}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-emerald-700">
                          {moneyOrDash(totals.value)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </CollapsibleSection>
        </div>
      )}

      <AwaitingInventoryTable />
    </>
  );
}
